// @flow
import * as React from 'react'
import styled from 'styled-components'
import { config, Hidden, Visible } from 'react-awesome-styled-grid'
import type { BookDetailPropsType, ChapterInfoPropsType } from '../../models/Book'
import { FadeInImage } from '@components/shared/FadeInImage'
import { colors, CTAPrimaryOrange } from '@components/shared'
import { TagPillRow } from '@components/Tag/TagPillRow'
import ChapterDownloadModal from '@components/Book/ChapterDownloadModal'

export function BookDetails (props: BookDetailPropsType): React.Node {
  const [modalVisible, setModalVisible] = React.useState<boolean>(false)
  const handleClose = (): void => {
    setModalVisible(false)
  }
  const openModal = (): void => {
    setModalVisible(true)
  }
  return (
    <BookDetailsContainer>
      <BookDetailsInfoContainer>
        <SmallImageContainer>
          <FadeInImage src={props.imageUrl} width={333} height={500} alt={props.title}/>
        </SmallImageContainer>
        <TitleAndInfoContainer>
          <TitleContainer>
            {props.title}
          </TitleContainer>
          <Hidden xs={true} sm={true} md={true}>
            <DescriptionContainer>
              {props.description}
            </DescriptionContainer>
            <TagPillRow small tags={props.tags} id={props.id}/>
            <LargePlayButton onClick={openModal}>Download the app to read</LargePlayButton>
          </Hidden>
        </TitleAndInfoContainer>
        <LargeImageContainer>
          <FadeInImage src={props.imageUrl} width={333} height={500} alt={props.title}/>
        </LargeImageContainer>
      </BookDetailsInfoContainer>
      <Visible xs={true} sm={true} md={true}>
          <DescriptionContainer>{props.description} </DescriptionContainer>
          <TagPillRow small tags={props.tags} id={props.id}/>
        <SmallTagAndButtonContainer>
          <LargePlayButton>Download the app to read</LargePlayButton>
        </SmallTagAndButtonContainer>
      </Visible>
      <Chapters>
        {props.chapters.map((chapter: ChapterInfoPropsType, index: number): React.Node => (
          <ChapterRow key={`book-${props.id}-chapter-${index}`} onClick={openModal}>
            <ChapterNumberContainer>{chapter.index}</ChapterNumberContainer>
              <ChapterTextContainer>
                <ChapterTitle>{chapter.title}</ChapterTitle>
                <ChapterDescription>{chapter.description}</ChapterDescription>
              </ChapterTextContainer>
          </ChapterRow>
        ))}
      </Chapters>
      <ChapterDownloadModal title={props.title} description={props.description} isVisible={modalVisible} handleClose={handleClose}/>
    </BookDetailsContainer>
  )
}

const ChapterRow = styled.div`
    display: flex;
    margin-bottom: 36px;
    ${(props: any): string => config(props).media.xs`
  `}
    ${(props: any): string => config(props).media.sm`
      border-top: 0.5px solid ${colors.white70};
      padding-top: 36px;
  `}
    ${(props: any): string => config(props).media.md`
  `}
    ${(props: any): string => config(props).media.lg`
    padding-top: 0;
    max-width: 49%;
    border-top: none;
    padding-bottom: 36px;
    border-bottom: 0.5px solid ${colors.white70};
  `}
    ${(props: any): string => config(props).media.xl`
  `}
`
const Chapters = styled.div`
    margin-top: 32px;
    ${(props: any): string => config(props).media.xs`
  `}
    ${(props: any): string => config(props).media.sm`
  `}
    ${(props: any): string => config(props).media.md`
  `}
    ${(props: any): string => config(props).media.lg`
      margin-top: 120px;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      gap: 40px;
      &>* {
          flex: 1 1 40%;
      }
  `}
    ${(props: any): string => config(props).media.xl`
      margin-top: 136px;
  `}
`
const SmallTagAndButtonContainer = styled.div`
    border-top: 0.5px solid ${colors.white};
    margin-top: 21px;
    ${(props: any): string => config(props).media.xs`
  `}
    ${(props: any): string => config(props).media.sm`
  `}
    ${(props: any): string => config(props).media.md`
  `}
    ${(props: any): string => config(props).media.lg`
  `}
    ${(props: any): string => config(props).media.xl`
  `}
`
const LargePlayButton = styled(CTAPrimaryOrange)`
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: "Good Sans Regular";
    font-size: 12px;
    font-style: normal;
    font-weight: 700;
    line-height: 100%;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    min-width: 250px;
    max-width: 350px;
    margin-top: 21px;
    
    ${(props: any): string => config(props).media.lg`
      font-size: 14px;
      margin-top: 36px;
    `}
    span {
        margin-right: 8px;
    }
`

const TitleAndInfoContainer = styled.div`
    display: flex;
    flex-direction: column;

    ${(props: any): string => config(props).media.xs`
  `}
    ${(props: any): string => config(props).media.sm`
      padding-top: 32px;
  `}
    ${(props: any): string => config(props).media.md`
      margin-right: 124px;
  `}
    ${(props: any): string => config(props).media.lg`
      padding-top: 60px;
    `}
    ${(props: any): string => config(props).media.xl`
      max-width: 50%;
      margin-right: 260px
    `}
`
const BookDetailsInfoContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
  
  ${(props: any): string => config(props).media.md`
    align-items: flex-start;
  `}
  ${(props: any): string => config(props).media.lg`
  flex-direction: row;
  `}
  ${(props: any): string => config(props).media.xl`
    padding-left: 36px;
  `}
`
const ChapterDescription = styled.div`
    font-family: "Good Sans Regular";
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 136%;
    letter-spacing: -0.3px;
    color: ${colors.white70};
  ${(props: any): string => config(props).media.xs`
  `}
  ${(props: any): string => config(props).media.sm`
  `}
  ${(props: any): string => config(props).media.md`
  `}
  ${(props: any): string => config(props).media.lg`
  font-size: 16px;
  `}
  ${(props: any): string => config(props).media.xl`
  `}
`
const ChapterTitle = styled.div`
    color: ${colors.white};
    font-family: "Good Sans Regular";
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 128%;
    letter-spacing: -0.3px;
    margin-bottom: 10px;
  ${(props: any): string => config(props).media.xs`
  `}
  ${(props: any): string => config(props).media.sm`
  `}
  ${(props: any): string => config(props).media.md`
  `}
  ${(props: any): string => config(props).media.lg`
    font-size: 18px;
  `}
  ${(props: any): string => config(props).media.xl`
  `}
`
const ChapterTextContainer = styled.div`
    display: flex;
    flex-direction: column;
    ${(props: any): string => config(props).media.xs`
  `}
    ${(props: any): string => config(props).media.sm`
  `}
    ${(props: any): string => config(props).media.md`
  `}
    ${(props: any): string => config(props).media.lg`
  `}
    ${(props: any): string => config(props).media.xl`
  `}
`
const ChapterNumberContainer = styled.div`
    color: ${colors.white};
    font-family: "Good Sans Regular";
    font-size: 25px;
    font-style: normal;
    font-weight: 500;
    line-height: 116%;
    width: 21px;
    margin-right: 32px;
    ${(props: any): string => config(props).media.xs`
  `}
    ${(props: any): string => config(props).media.sm`
  `}
    ${(props: any): string => config(props).media.md`
      font-size: 33px;
  `}
    ${(props: any): string => config(props).media.lg`
      font-size: 40px;
  `}
    ${(props: any): string => config(props).media.xl`
  `}
`

const DescriptionContainer = styled.div`
    font-family: "Good Sans Regular";
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%;
    color: ${colors.white};
    margin-bottom: 20px;
    margin-top: 16px;

  ${(props: any): string => config(props).media.sm`
    margin-top: 20px;
    font-size: 14px;
  `}
  ${(props: any): string => config(props).media.md`
    font-size: 16px;
  `}
  ${(props: any): string => config(props).media.lg`
  `}
  ${(props: any): string => config(props).media.xl`
  `}
`
const TitleContainer = styled.div`
    font-family: "Queens Condensed Light";
    font-size: 32px;
    font-style: normal;
    font-weight: 400;
    line-height: 110%;
    color: ${colors.white};
    margin: 20px 0;
    ${(props: any): string => config(props).media.xs`
  `}
    ${(props: any): string => config(props).media.sm`
      font-size: 28px;
  `}
    ${(props: any): string => config(props).media.md`
      font-size: 45px;
    `}
    ${(props: any): string => config(props).media.lg`
  `}
    ${(props: any): string => config(props).media.xl`
  `}
`

const LargeImageContainer = styled.div`
    display: none;
    flex-shrink: 0;

    ${(props: any): string => config(props).media.md`
      display: none;
  `}
    ${(props: any): string => config(props).media.lg`
      display: flex;
      width: 333px;
      height: 500px;
  `}
    ${(props: any): string => config(props).media.xl`
  `}
`

const SmallImageContainer = styled.div`
    width: 200px;
    height: 300px;
    flex-shrink: 0;
    margin-top: 16px;
    margin-right: 12px;

    ${(props: any): string => config(props).media.sm`
      margin-right: 20px; 
    `}

    ${(props: any): string => config(props).media.lg`
      display: none;  
  `}
`

const BookDetailsContainer = styled.div`
    display: flex;
    flex-direction: column;

    ${(props: any): string => config(props).media.xs`
  `}
    ${(props: any): string => config(props).media.sm`
  `}
    ${(props: any): string => config(props).media.md`
  `}
    ${(props: any): string => config(props).media.lg`
    margin-top: 60px;
  `}
    ${(props: any): string => config(props).media.xl`
  `}
`
